import { ComponentPropsWithRef, ReactNode } from "react";
import styled, { css } from "styled-components";
import { staticText } from "../../shared/statics/static-text";
import {
  elevation00,
  semanticRed02,
  shade0,
  shade300,
  shade600,
  shade700,
  shade800,
} from "../../styles/tokens";
import { normalStyles, xSmallStyles } from "../body/Body.Parts";

// TEXT
export const text = staticText.input;

// STYLES
const wrapperFocusStyles = css`
  border: 1px solid ${shade0};
  label {
    ${xSmallStyles};
    color: ${shade300};
  }
`;

export const Wrapper = styled.span<WrapperProps>`
  ${({ $fullWidth: fw = true }) => fw && "width: 100%;"}

  position: relative;
  display: flex;
  flex-direction: row;
  padding: 0 1rem;
  height: 4rem;

  ${normalStyles};

  transition:
    0.3s ease border-color,
    0.3s ease box-shadow;
  label {
    transition:
      font-size 0.3s ease,
      line-height 0.3s ease,
      color 0.3s ease;
  }

  ${({ $disabled, $hasError, $emulateFocus }) =>
    $disabled
      ? css`
          cursor: not-allowed;
          color: ${shade700};
          border: 1px solid ${shade800};
        `
      : css`
          border: 1px solid ${$hasError ? semanticRed02 : shade800};

          &:hover {
            box-shadow: ${elevation00};
            ${!$hasError && `border-color: ${shade700}`};
          }

          &:focus-within {
            ${wrapperFocusStyles}
          }

          ${$emulateFocus && wrapperFocusStyles}
        `}

  ${({ $hasContent }) =>
    $hasContent &&
    css`
      label {
        ${xSmallStyles};
        color: ${shade300};
      }
    `}
`;

export const InnerWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const innerInputFocusStyles = css`
  max-height: 1.5rem;
  opacity: 1;
`;

export const InnerInput = styled.input<InnerInputProps>`
  width: 100%;
  padding-right: 1rem;
  padding-left: 0;
  text-overflow: ellipsis;
  outline: none;

  max-height: ${({ $hasContent }) => ($hasContent ? "1.5rem" : "0")};
  opacity: ${({ $hasContent }) => ($hasContent ? "1" : "0")};

  ${normalStyles};
  color: ${shade0};

  transition:
    max-height 0.3s ease,
    opacity 0.8s ease;

  &::placeholder {
    color: ${shade600};
  }

  &:focus {
    ${innerInputFocusStyles};
  }

  ${({ $emulateFocus }) => $emulateFocus && innerInputFocusStyles};
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
`;

// DATA

// TYPES
export type CustomAction = {
  onClick: (value: string) => void;
  icon: ReactNode;
  disabled?: boolean | ((value: string) => boolean);
};

export interface Props extends ComponentPropsWithRef<"input"> {
  /**
   * Label for the input field
   */
  label?: string;
  /**
   * ID for the input field
   */
  id?: string;
  /**
   * Emulate an error state on the input
   */
  hasError?: boolean;
  /**
   * Disable the input field
   */
  disabled?: boolean;
  /**
   * Make the input take the full width of its container
   */
  fullWidth?: boolean;
  /**
   * Emulate focus styles on the input
   */
  emulateFocus?: boolean;
  /**
   * Allow the input value to be copyable
   */
  copyable?: boolean;
  /**
   * Custom actions to be displayed inside the input
   */
  customActions?: CustomAction[];
}

type WrapperProps = {
  $hasError?: boolean;
  $disabled?: boolean;
  $fullWidth?: boolean;
  $hasContent?: boolean;
  $emulateFocus?: boolean;
};

type InnerInputProps = Pick<WrapperProps, "$hasContent" | "$emulateFocus"> & {
  disabled?: boolean; // Pass-through for native input disabled prop
};

// CONSTANTS

// FUNCTIONS

// ASSETS
